import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Target, Gauge, Users, Award, Lightbulb, CheckSquare, Star,
  Plus, ArrowRight, TrendingUp, MessageSquare, Sparkles,
} from "lucide-react";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, StatusBadge, SectionCard, PillTabs, EmptyState,
} from "@/components/app/PageKit";

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

const NINE_BOX = [
  { label: "Diamond",       perf: 0, pot: 2, tone: "from-cyan-100 to-cyan-50 text-cyan-800 ring-cyan-200" },
  { label: "Future Leader", perf: 1, pot: 2, tone: "from-sky-100 to-sky-50 text-sky-800 ring-sky-200" },
  { label: "Star",          perf: 2, pot: 2, tone: "from-violet-200 to-fuchsia-100 text-violet-900 ring-violet-300", featured: true },
  { label: "Effective",     perf: 0, pot: 1, tone: "from-yellow-100 to-yellow-50 text-yellow-800 ring-yellow-200" },
  { label: "Core Player",   perf: 1, pot: 1, tone: "from-lime-100 to-lime-50 text-lime-800 ring-lime-200" },
  { label: "High Impact",   perf: 2, pot: 1, tone: "from-emerald-100 to-emerald-50 text-emerald-800 ring-emerald-200" },
  { label: "Risk",          perf: 0, pot: 0, tone: "from-rose-100 to-rose-50 text-rose-800 ring-rose-200" },
  { label: "Inconsistent",  perf: 1, pot: 0, tone: "from-orange-100 to-orange-50 text-orange-800 ring-orange-200" },
  { label: "Solid",         perf: 2, pot: 0, tone: "from-amber-100 to-amber-50 text-amber-800 ring-amber-200" },
];

// Sample employee distribution (deterministic mock)
const samplePeople = [
  { id: "e1", name: "Arjun Mehta",     box: "Star",         perfPct: 92, potPct: 95 },
  { id: "e2", name: "Riya Malhotra",   box: "High Impact",  perfPct: 88, potPct: 65 },
  { id: "e3", name: "Sneha Iyer",      box: "Future Leader",perfPct: 65, potPct: 92 },
  { id: "e4", name: "Vikram Singh",    box: "Core Player",  perfPct: 72, potPct: 60 },
  { id: "e5", name: "Karan Joshi",     box: "Effective",    perfPct: 45, potPct: 55 },
  { id: "e6", name: "Priya Sharma",    box: "Solid",        perfPct: 90, potPct: 30 },
  { id: "e7", name: "Anjali Desai",    box: "Diamond",      perfPct: 35, potPct: 88 },
  { id: "e8", name: "Rahul Verma",     box: "Inconsistent", perfPct: 55, potPct: 28 },
  { id: "e9", name: "Tanvi Joshi",     box: "Core Player",  perfPct: 68, potPct: 55 },
  { id: "e10",name: "Neha Sharma",     box: "Risk",         perfPct: 28, potPct: 22 },
];

export default function PerformancePro() {
  const [tab, setTab] = useState("overview");
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.get("/goals").then(({ data }) => setGoals(data || [])).catch(() => setGoals([]));
    api.get("/reviews").then(({ data }) => setReviews(data || [])).catch(() => setReviews([]));
    api.get("/employees").then(({ data }) => setEmployees(data || [])).catch(() => setEmployees([]));
  }, []);

  const empByName = (n) => employees.find(e => (e.full_name || "").toLowerCase() === (n || "").toLowerCase());
  const peopleByBox = samplePeople.reduce((acc, p) => { (acc[p.box] = acc[p.box] || []).push(p); return acc; }, {});

  // KPIs
  const totalGoals = goals.length || 24;
  const onTrack = Math.round(totalGoals * 0.7);
  const cycleStarted = reviews.length || 12;
  const pendingSelf = Math.max(0, 8 - reviews.filter(r => r.status === "completed").length);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="performance-pro-page">
      <PageHeader
        category="TALENT"
        title="Performance Pro"
        badge="Basic Version"
        description="OKRs, 360° feedback, 9-box talent matrix, and AI-grounded reviews — all in one workspace."
        primaryAction={{ label: "Create Review Cycle", icon: Plus, onClick: () => {} }}
        secondaryAction={{ label: "Set OKRs", icon: Target, onClick: () => {} }}
        moreActions={[
          { label: "Calibration Session",   icon: Gauge,         onClick: () => {} },
          { label: "PIP Workspace",         icon: TrendingUp,    onClick: () => {} },
          { label: "Recognition Wall",      icon: Award,         onClick: () => {} },
        ]}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={Target}        tone="violet"   label="Active Goals"        value={totalGoals}                   sub="Across teams"             testid="pp-kpi-goals" />
        <KPICard icon={TrendingUp}    tone="emerald"  label="On-Track"            value={`${onTrack}/${totalGoals}`}   sub={`${Math.round(onTrack/totalGoals*100)}% healthy`} testid="pp-kpi-ontrack" />
        <KPICard icon={Users}         tone="blue"     label="Cycle Started"       value={cycleStarted}                 sub="Q2 reviews"               testid="pp-kpi-cycle" />
        <KPICard icon={CheckSquare}   tone="amber"    label="Pending Self-Review" value={pendingSelf}                  sub="Awaiting employee"        testid="pp-kpi-pending" />
        <KPICard icon={Award}         tone="fuchsia"  label="Stars (9-Box)"       value={peopleByBox.Star?.length || 0} sub="Top-right quadrant"      testid="pp-kpi-stars" />
      </KPIGrid>

      <PillTabs
        value={tab} onChange={setTab}
        tabs={[
          { value: "overview",  label: "Overview" },
          { value: "ninebox",   label: "9-Box Talent Matrix" },
          { value: "goals",     label: "Goals & OKRs", badge: goals.length },
          { value: "reviews",   label: "Reviews",      badge: reviews.length },
          { value: "feedback",  label: "360° Feedback" },
        ]}
        testid="pp-tabs"
      />

      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0 space-y-5">
          {tab === "overview" && (
            <>
              {/* 9-box compact preview */}
              <SectionCard icon={Gauge} title="Talent Distribution" sub="Performance × Potential">
                <div className="grid grid-cols-3 gap-3">
                  {[2, 1, 0].flatMap((pot) => [0, 1, 2].map((perf) => {
                    const cell = NINE_BOX.find(c => c.perf === perf && c.pot === pot);
                    const ppl = peopleByBox[cell.label] || [];
                    return (
                      <div key={cell.label} className={`aspect-[4/3] rounded-xl bg-gradient-to-br ${cell.tone} ring-1 ${cell.featured ? "ring-2" : ""} p-3 flex flex-col justify-between min-w-0`} data-testid={`box-${cell.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] font-bold uppercase tracking-wider truncate flex-1">{cell.label}</div>
                          {cell.featured && <Sparkles className="w-3 h-3 shrink-0" />}
                        </div>
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex -space-x-1.5">
                            {ppl.slice(0, 3).map(p => (
                              <Avatar key={p.id} className="w-5 h-5 ring-2 ring-white">
                                <AvatarFallback className="text-[8px] font-bold bg-white/40 text-current">{initials(p.name)}</AvatarFallback>
                              </Avatar>
                            ))}
                          </div>
                          <span className="text-[11px] font-bold tabular-nums">{ppl.length}</span>
                        </div>
                      </div>
                    );
                  }))}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-[10px] uppercase tracking-wider font-bold text-slate-400 text-center">
                  <div>Low Performance</div><div>Mid</div><div>High Performance</div>
                </div>
              </SectionCard>

              {/* Performance distribution */}
              <SectionCard icon={TrendingUp} title="Performance Distribution" sub="Last review cycle">
                <div className="space-y-2.5">
                  {[
                    { label: "Exceeds Expectations", count: 8,  pct: 16, color: "bg-emerald-500" },
                    { label: "Meets Expectations",   count: 32, pct: 64, color: "bg-blue-500"    },
                    { label: "Partially Meets",      count: 7,  pct: 14, color: "bg-amber-500"   },
                    { label: "Below Expectations",   count: 3,  pct: 6,  color: "bg-rose-500"    },
                  ].map((d) => (
                    <div key={d.label}>
                      <div className="flex items-center justify-between text-[12px] mb-1">
                        <span className="font-semibold text-slate-700">{d.label}</span>
                        <span className="text-slate-500 tabular-nums">{d.count} · {d.pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${d.color}`} style={{ width: `${d.pct * 3}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </>
          )}

          {tab === "ninebox" && (
            <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="pp-ninebox-full">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-3">9-Box Talent Matrix · Performance × Potential</div>
              <div className="relative">
                <div className="absolute -left-6 top-0 bottom-12 flex flex-col items-center justify-between text-[10px] uppercase font-bold text-slate-400 [writing-mode:vertical-rl] rotate-180">
                  <span>High Potential</span><span>Mid</span><span>Low Potential</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[2, 1, 0].flatMap((pot) => [0, 1, 2].map((perf) => {
                    const cell = NINE_BOX.find(c => c.perf === perf && c.pot === pot);
                    const ppl = peopleByBox[cell.label] || [];
                    return (
                      <div key={cell.label} className={`min-h-[140px] rounded-xl bg-gradient-to-br ${cell.tone} ring-1 ${cell.featured ? "ring-2" : ""} p-3 flex flex-col gap-2 min-w-0`}>
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] font-bold uppercase tracking-wider">{cell.label}</div>
                          <span className="text-[11px] font-bold bg-white/60 px-1.5 py-0.5 rounded-full">{ppl.length}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {ppl.slice(0, 4).map(p => {
                            const emp = empByName(p.name);
                            return (
                              <div key={p.id} className="inline-flex items-center gap-1.5 bg-white/70 rounded-full pr-2 pl-0.5 py-0.5" title={`${p.name} · perf ${p.perfPct} · pot ${p.potPct}`}>
                                <Avatar className="w-5 h-5 ring-1 ring-white">
                                  {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={p.name} />}
                                  <AvatarFallback className="text-[8px] font-bold bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">{initials(p.name)}</AvatarFallback>
                                </Avatar>
                                <span className="text-[10px] font-semibold text-slate-700">{p.name.split(" ")[0]}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }))}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-[10px] uppercase tracking-wider font-bold text-slate-400 text-center">
                  <div>Low Performance</div><div>Mid</div><div>High Performance</div>
                </div>
              </div>
            </Card>
          )}

          {tab === "goals" && (
            <SectionCard icon={Target} title="Goals & OKRs" sub={`${goals.length} active`}>
              {goals.length === 0 ? (
                <EmptyState icon={Target} title="No goals defined yet" message="Set quarterly OKRs to align teams and track outcomes." action={{ label: "Set OKRs", icon: Plus, onClick: () => {} }} />
              ) : (
                <div className="space-y-2">
                  {goals.slice(0, 10).map((g, i) => (
                    <div key={g.id || i} className="p-3 rounded-lg border border-slate-100 hover:border-violet-200">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold text-slate-900 truncate">{g.title || `Goal ${i + 1}`}</span>
                        <StatusBadge status={g.status || "in_progress"} />
                      </div>
                      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${g.progress || 50}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {(tab === "reviews" || tab === "feedback") && (
            <EmptyState icon={tab === "reviews" ? CheckSquare : MessageSquare} title={tab === "reviews" ? "Reviews workspace" : "360° feedback"}
              message="Launch a new cycle, request feedback, and track responses across self / peer / manager / skip-level." action={{ label: "Create Cycle", icon: Plus, onClick: () => {} }} />
          )}
        </div>

        {/* Right Rail */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={CheckSquare} title="Pending Self-Reviews" sub={`${pendingSelf} awaiting`}>
            <div className="space-y-2.5">
              {samplePeople.slice(0, 4).map(p => {
                const emp = empByName(p.name);
                return (
                  <div key={p.id} className="flex items-center gap-2.5">
                    <Avatar className="w-9 h-9 ring-2 ring-white">
                      {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={p.name} />}
                      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(p.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-slate-900 truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500">Due in 3 days</div>
                    </div>
                    <Button size="sm" variant="outline" className="border-slate-200 h-7 px-2 text-[10px]">Remind</Button>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard icon={Lightbulb} title="AI Insights" sub="Powered by Claude">
            <div className="space-y-2 text-[12px] text-slate-700">
              <div className="p-2.5 rounded-lg bg-violet-50 ring-1 ring-violet-100">
                <span className="font-bold">3 goals at risk</span> — engagement scores dropping in Eng team.
              </div>
              <div className="p-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-100">
                <span className="font-bold">2 hidden Stars</span> identified via peer feedback patterns.
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-50 ring-1 ring-emerald-100">
                <span className="font-bold">Recognition gap</span> in Sales — only 12% kudos last week.
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={Award} title="Recent Recognition" sub="Peer kudos">
            <div className="space-y-2">
              {[
                { from: "Riya", to: "Sneha",  msg: "Crushed the migration!" },
                { from: "Vikram", to: "Karan", msg: "Saved us 4 hours debugging" },
                { from: "Priya", to: "Anjali", msg: "Best mentor of the quarter" },
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50">
                  <Star className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                  <div className="text-[11px] text-slate-700">
                    <span className="font-bold">{r.from}</span> → <span className="font-bold">{r.to}</span>
                    <div className="text-slate-500 italic">"{r.msg}"</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}
